import { PureComponent, ReactElement, useContext } from 'react';

// Extend the HTMLAudioElement interface to include captureStream
interface HTMLAudioElement {
  captureStream?: () => MediaStream;
  mozCaptureStream?: () => MediaStream;
}
import { RemoteParticipant } from 'twilio-video';
import firebase from 'firebase/app';
import 'firebase/firestore';
// Import AuthContext to access auth.role
import { AuthContext } from '../auth';
import {
  Participant,
  Room,
  connect,
  LocalVideoTrack,
  LocalAudioTrack,
  LocalTrack,
  LocalAudioTrackPublication,
  LocalVideoTrackPublication,
  createLocalAudioTrack
} from 'twilio-video';
import CallContext, { CallStatus } from './context';
import { createLocalVideoTrack } from 'twilio-video';

interface State {
  status: CallStatus;
  room: Room | null;
  participants: RemoteParticipant[];
  muted: boolean;
  camera: boolean;
  screenSharing: boolean;
  screenTrack: LocalVideoTrack | null;
  error: Error | null;
  systemAudioTrack: LocalAudioTrack | null;
  audioId: string | null;
  mutedAll: boolean;
}

interface Props {
  auth?: {
    role?: 'student' | 'tutor' | 'admin';
    user?: firebase.User | null;
    // Add other auth properties as needed
  };
  children?: React.ReactNode;
}

class CallProvider extends PureComponent<Props, State> {
  unsubscribeMuteAllListener: (() => void) | null = null;
  muteAllCheckInterval: NodeJS.Timeout | null = null;

  componentDidMount() {
    // Listen for muteAll changes if in a room
    this.setupMuteAllListener();
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    // If room changes, reset the listener
    if (this.state.room?.name !== prevState.room?.name) {
      this.cleanupMuteAllListener();
      this.setupMuteAllListener();
    }
  }

  componentWillUnmount() {
    this.cleanupMuteAllListener();
  }

  setupMuteAllListener() {
    const { room } = this.state;
    const { auth } = this.props;

    if (!room?.name) {
      console.log('No room available to setup muteAll listener');
      return;
    }

    const roomId = room.name;
    console.log('Setting up muteAll listener for room:', roomId);

    // Clean up any existing listener first
    this.cleanupMuteAllListener();

    // Set up Firestore listener
    this.unsubscribeMuteAllListener = firebase
      .firestore()
      .collection('rooms')
      .doc(roomId)
      .onSnapshot(
        (doc) => {
          console.log('MuteAll listener triggered');
          const data = doc.data();

          if (!data) {
            console.log('No data found in room document');
            return;
          }

          console.log('Room data:', data);
          console.log('User role:', auth?.role);
          console.log('MuteAll status:', data.muteAll);

          this.handleMuteAllChange(data.muteAll, auth?.role);
        },
        (error) => {
          console.error('Error listening to muteAll changes:', error);
        }
      );

    // Set up periodic verification (more reliable interval clearing)
    this.muteAllCheckInterval = setInterval(() => {
      this.verifyMuteAllState(roomId, auth?.role);
    }, 5000);
  }

  handleMuteAllChange(muteAll: boolean, userRole?: string) {
    const { room } = this.state;

    if (!room?.localParticipant) {
      console.log('No local participant available');
      return;
    }

    console.log('Handling muteAll change:', { muteAll, userRole });

    if (muteAll && userRole !== 'tutor') {
      // Mute the user if they're not a tutor/admin
      room.localParticipant.audioTracks.forEach((publication) => {
        if (publication.track && publication.track.isEnabled) {
          console.log('Disabling audio track due to muteAll');
          publication.track.disable();
        }
      });

      this.setState({
        mutedAll: true,
        muted: true
      });
    } else if (!muteAll) {
      // Reset muteAll state when it's disabled
      console.log('MuteAll disabled, resetting state');
      this.setState({
        mutedAll: false
      });
      // Note: Don't automatically unmute users, let them control their own mute state
    }
  }

  verifyMuteAllState(roomId: string, userRole?: string) {
    firebase
      .firestore()
      .collection('rooms')
      .doc(roomId)
      .get()
      .then((doc) => {
        const data = doc.data();
        if (!data) return;

        console.log('Verifying muteAll state:', data.muteAll);

        if (data.muteAll && userRole !== 'tutor') {
          const { room } = this.state;
          if (!room?.localParticipant) return;

          // Force mute if any audio tracks are enabled
          let foundEnabledTrack = false;
          room.localParticipant.audioTracks.forEach((publication) => {
            if (publication.track && publication.track.isEnabled) {
              console.log('Force disabling audio track during verification');
              publication.track.disable();
              foundEnabledTrack = true;
            }
          });

          if (foundEnabledTrack) {
            this.setState({ muted: true, mutedAll: true });
          }
        }
      })
      .catch((error) => {
        console.error('Error verifying muteAll state:', error);
      });
  }

  cleanupMuteAllListener() {
    console.log('Cleaning up muteAll listener');

    if (this.unsubscribeMuteAllListener) {
      this.unsubscribeMuteAllListener();
      this.unsubscribeMuteAllListener = null;
    }

    if (this.muteAllCheckInterval) {
      clearInterval(this.muteAllCheckInterval);
      this.muteAllCheckInterval = null;
    }
  }

  constructor(props: Props) {
    super(props);

    this.state = {
      status: CallStatus.Disconnected,
      room: null,
      participants: [],
      muted: false,
      camera: true,
      screenSharing: false,
      screenTrack: null,
      error: null,
      systemAudioTrack: null,
      audioId: null,
      mutedAll: false
    };

    this.toggleMute = this.toggleMute.bind(this);
    this.switchMic = this.switchMic.bind(this);
    this.toggleCamera = this.toggleCamera.bind(this);
    this.switchCamera = this.switchCamera.bind(this);
    this.participantConnected = this.participantConnected.bind(this);
    this.participantDisconnected = this.participantDisconnected.bind(this);
    this.leave = this.leave.bind(this);
    this.join = this.join.bind(this);
    this.startScreenSharing = this.startScreenSharing.bind(this);
    this.stopScreenSharing = this.stopScreenSharing.bind(this);
    this.startAudioShare = this.startAudioShare.bind(this);
    this.muteAllRemoteParticipants = this.muteAllRemoteParticipants.bind(this);
    this.unmuteAllRemoteParticipants =
      this.unmuteAllRemoteParticipants.bind(this);
  }

  toggleMute(): void {
    const { auth } = this.props;
    const { room, muted, mutedAll } = this.state;

    // Prevent non-tutors from unmuting when muteAll is active
    if (auth?.role !== 'tutor' && auth?.role !== 'admin' && mutedAll && muted) {
      console.log(
        'Cannot unmute: muteAll is active and user is not tutor/admin'
      );
      return;
    }

    if (!room?.localParticipant) {
      console.log('No local participant available for toggle mute');
      return;
    }

    room.localParticipant.audioTracks.forEach((publication) => {
      if (publication.track) {
        if (muted) {
          console.log('Enabling audio track');
          publication.track.enable();
        } else {
          console.log('Disabling audio track');
          publication.track.disable();
        }
      }
    });

    this.setState({
      muted: !muted
    });
  }

  muteAllRemoteParticipants(): void {
    const { room } = this.state;
    const { auth } = this.props;

    // Only allow tutors and admins to mute all participants
    if (auth?.role !== 'tutor' && auth?.role !== 'admin') {
      console.log('Only tutors and admins can mute all participants');
      return;
    }

    if (!room?.name) {
      console.log('No room available to mute all participants');
      return;
    }

    console.log('Muting all remote participants');

    // Update Firestore to trigger the listener for all participants
    firebase
      .firestore()
      .collection('rooms')
      .doc(room.name)
      .set({ muteAll: true }, { merge: true })
      .then(() => {
        console.log('MuteAll set to true in Firestore');
        this.setState({ mutedAll: true });
      })
      .catch((error) => {
        console.error('Error setting muteAll in Firestore:', error);
      });

    // Also mute remote participants locally (so tutor doesn't hear them)
    room.participants.forEach((participant) => {
      participant.audioTracks.forEach((publication) => {
        if (publication.track) {
          publication.track.detach();
          console.log(`Locally muted ${participant.identity}'s audio`);
        }
      });
    });
  }

  unmuteAllRemoteParticipants(): void {
    const { room } = this.state;
    const { auth } = this.props;

    // Only allow tutors and admins to unmute all participants
    if (auth?.role !== 'tutor' && auth?.role !== 'admin') {
      console.log('Only tutors and admins can unmute all participants');
      return;
    }

    if (!room?.name) {
      console.log('No room available to unmute all participants');
      return;
    }

    console.log('Unmuting all remote participants');

    // Update Firestore to trigger the listener for all participants
    firebase
      .firestore()
      .collection('rooms')
      .doc(room.name)
      .set({ muteAll: false }, { merge: true })
      .then(() => {
        console.log('MuteAll set to false in Firestore');
        this.setState({ mutedAll: false });
      })
      .catch((error) => {
        console.error('Error unsetting muteAll in Firestore:', error);
      });

    // Also unmute remote participants locally
    room.participants.forEach((participant) => {
      participant.audioTracks.forEach((publication) => {
        if (publication.track) {
          publication.track.attach();
          console.log(`Locally unmuted ${participant.identity}'s audio`);
        }
      });
    });
  }

  async switchMic(
    deviceId: string
  ): Promise<LocalAudioTrackPublication | null> {
    const { room } = this.state;
    if (!room?.localParticipant)
      throw new Error('Cannot switch camera when not in a room!');

    try {
      const trackPubs = Array.from(room.localParticipant.audioTracks.values());
      if (trackPubs.length > 0) {
        await trackPubs[0].track.restart({ deviceId });
      }
      return trackPubs[0];
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  toggleCamera(): void {
    const { room, camera } = this.state;

    room?.localParticipant?.videoTracks.forEach((publication) => {
      if (publication.track.name !== 'screen-share') {
        if (camera) {
          publication.track.disable();
        } else {
          publication.track.enable();
        }
      }
    });

    this.setState({
      camera: !this.state.camera
    });
  }

  async switchCamera(
    deviceId: string
  ): Promise<LocalVideoTrackPublication | null> {
    const { room } = this.state;
    if (!room?.localParticipant)
      throw new Error('Cannot switch camera when not in a room!');

    try {
      const trackPubs = Array.from(room.localParticipant.videoTracks.values());
      if (trackPubs.length > 0) {
        await trackPubs[0].track.restart({ deviceId });
      }
      return trackPubs[0];
    } catch (error) {
      console.error(error);
    }

    return null;
  }

  async startScreenSharing(): Promise<void> {
    try {
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      let options: any;

      if (isMobile) {
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
          options = { video: true };
        } else {
          options = { video: { displaySurface: 'browser' } };
        }
      } else {
        // Use simple video: true to show full browser dialog with all options:
        // Entire Screen, Window, and Chrome Tab options
        options = {
          video: true
        };
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        alert('Screen sharing is not supported in this browser or device');
        return;
      }

      const stream = await navigator.mediaDevices.getDisplayMedia(options);
      const videoTrack = stream.getVideoTracks()[0];

      if (!videoTrack) {
        throw new Error('No video track found in screen sharing stream');
      }

      const screenTrack = new LocalVideoTrack(videoTrack, {
        name: 'screen-share'
      });

      this.setState({ screenSharing: true, screenTrack });

      screenTrack.mediaStreamTrack.onended = () => {
        this.stopScreenSharing();
      };

      const { room } = this.state;
      if (room) {
        await room.localParticipant.publishTrack(screenTrack);
        console.log('Screen sharing started successfully');
      } else {
        console.warn('No room available to publish screen track');
        this.stopScreenSharing();
      }
    } catch (error) {
      console.error('Error starting screen sharing:', error);
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        alert('Screen sharing permission was denied');
      } else if (
        error instanceof DOMException &&
        error.name === 'NotSupportedError'
      ) {
        alert('Screen sharing is not supported on this device or browser');
      }
      this.setState({ screenSharing: false, screenTrack: null });
    }
  }

  async stopScreenSharing(): Promise<void> {
    const { screenTrack, room } = this.state;

    if (screenTrack) {
      screenTrack.stop();

      if (room) {
        const trackPublication =
          room.localParticipant.unpublishTrack(screenTrack);
        room.localParticipant.emit('trackUnpublished', trackPublication);
      }

      this.setState({ screenSharing: false, screenTrack: null });
    }
  }

  participantConnected(participant: Participant): void {
    const { participants } = this.state;

    console.log(`Participant ${participant.identity} connected!`);
    this.setState({
      participants: [...participants, participant as RemoteParticipant]
    });
  }

  participantDisconnected(participant: Participant): void {
    const { participants } = this.state;

    console.log(`Participant ${participant.identity} disconnected!`);
    this.setState({
      participants: participants.filter((p) => p !== participant)
    });
  }

  leave(): void {
    const { room } = this.state;

    // Clean up muteAll listener when leaving
    this.cleanupMuteAllListener();

    if (room && room.localParticipant.state === 'connected') {
      room.localParticipant.tracks.forEach(function ({ track }) {
        if (track instanceof LocalVideoTrack) {
          (track as LocalVideoTrack).stop();
        } else if (track instanceof LocalAudioTrack) {
          (track as LocalAudioTrack).stop();
        }
      });

      room.disconnect();
    }

    this.setState({
      status: CallStatus.Disconnected,
      room: null,
      muted: false,
      camera: false,
      participants: [],
      error: null,
      screenSharing: false,
      screenTrack: null,
      mutedAll: false
    });
  }

  countdownTimer() {
    const countdownDateTime = new Date().getTime() + 900000;
    const completedLessonTime =
      Math.round((Date.now() - 3600000) / 1000 / 60 / 60) * 60 * 60 * 1000;

    const timeInterval = setInterval(async () => {
      const currentTime = new Date().getTime();
      const remainingDayTime = countdownDateTime - currentTime;

      if (remainingDayTime < 0) {
        const completedLessons = await firebase
          .firestore()
          .collectionGroup('bookings')
          .where('status', '==', 'in-progress')
          .where('when', '<=', completedLessonTime)
          .get();

        if (!completedLessons.empty) {
          completedLessons.docs.forEach((doc) => {
            doc.ref.update({ status: 'tutor-present' });
          });
        }
        clearInterval(timeInterval);
      }
    }, 1000);
  }

  async join(
    token: string,
    roomName: string,
    tracks?: LocalTrack[]
  ): Promise<void> {
    try {
      console.log(`Connecting to ${roomName} room...`, token);

      this.setState({ status: CallStatus.Connecting });

      if (!tracks || tracks.length === 0) {
        console.log('No tracks found, creating new tracks...');
        const audioTrack = await createLocalAudioTrack();
        const videoTrack = await createLocalVideoTrack();
        tracks = [audioTrack, videoTrack];
      }

      const room = await connect(token, {
        name: roomName,
        tracks
      });

      console.log(`Connected to ${roomName} room!`);
      this.countdownTimer();

      room.on('participantConnected', this.participantConnected);
      room.on('participantDisconnected', this.participantDisconnected);

      const localVideoTrack = tracks?.find(
        (track) => track.kind === 'video'
      ) as LocalVideoTrack;
      const localAudioTrack = tracks?.find(
        (track) => track.kind === 'audio'
      ) as LocalAudioTrack;

      if (localAudioTrack) localAudioTrack.disable();
      if (localVideoTrack) localVideoTrack.disable();

      this.setState(
        {
          status: CallStatus.Connected,
          participants: Array.from(room.participants.values()),
          camera: false,
          muted: true,
          room
        },
        () => {
          // Setup muteAll listener after room is set in state
          this.setupMuteAllListener();
        }
      );

      return;
    } catch (error: any) {
      this.setState({ error });
      throw error;
    }
  }

  async startAudioShare(id: string): Promise<void> {
    try {
      const { systemAudioTrack, audioId } = this.state;

      if (systemAudioTrack && audioId === id) {
        console.log('Audio is already being shared');
        return;
      }

      if (audioId !== id) {
        if (systemAudioTrack) {
          systemAudioTrack.stop();
          const { room } = this.state;
          if (room) {
            const trackPublication =
              room.localParticipant.unpublishTrack(systemAudioTrack);
            room.localParticipant.emit('trackUnpublished', trackPublication);
          }
        }
        this.setState({ systemAudioTrack: null, audioId: null });
      }

      const audioElement = document.getElementById(id) as HTMLAudioElement;

      if (!audioElement) {
        console.error(`Audio element not found with ID "${id}"`);
        return;
      }

      if (!(window as any)._sharedAudioContext) {
        const AudioContext =
          window.AudioContext || (window as any).webkitAudioContext;
        (window as any)._sharedAudioContext = new AudioContext();
      }
      const audioContext = (window as any)._sharedAudioContext;

      if ((audioElement as HTMLMediaElement).readyState < 2) {
        await new Promise<void>((resolve) => {
          (audioElement as HTMLMediaElement).addEventListener(
            'canplay',
            () => resolve(),
            { once: true }
          );
          (audioElement as HTMLMediaElement).load();
        });
      }

      try {
        (audioElement as HTMLMediaElement).muted = false;
        (audioElement as HTMLMediaElement).volume = 1.0;

        const playPromise = (audioElement as HTMLMediaElement).play();
        if (playPromise !== undefined) {
          await playPromise;
          console.log('Audio successfully playing');
        }
      } catch (playError) {
        console.error('Failed to play audio automatically:', playError);
        return;
      }

      let source: MediaElementAudioSourceNode;

      if ((audioElement as any)._sourceNode) {
        source = (audioElement as any)._sourceNode;
        console.log('Reusing existing MediaElementSourceNode for', id);
      } else {
        source = audioContext.createMediaElementSource(audioElement);
        (audioElement as any)._sourceNode = source;
        console.log('Created new MediaElementSourceNode for', id);
      }

      const destination = audioContext.createMediaStreamDestination();
      source.connect(destination);
      source.connect(audioContext.destination);

      const contextAudioTrack = destination.stream.getAudioTracks()[0];

      if (!contextAudioTrack) {
        console.error('No audio tracks found in the destination stream.');
        return;
      }

      const newsystemAudioTrack = new LocalAudioTrack(contextAudioTrack, {
        name: 'system-audio'
      });
      newsystemAudioTrack.enable();

      const { room } = this.state;

      if (!room || !room.localParticipant) {
        console.error('Cannot share audio: Not connected to a room');
        return;
      }

      const existingPublication = Array.from(
        room.localParticipant.audioTracks.values()
      ).find((pub) => pub.trackName === 'system-audio');

      if (existingPublication) {
        console.log('Replacing existing system audio track');
        existingPublication.unpublish();
      }

      const trackPublication = await room.localParticipant.publishTrack(
        newsystemAudioTrack,
        { priority: 'high' }
      );

      console.log('Audio sharing started successfully', {
        trackSid: trackPublication.trackSid
      });

      this.setState({ systemAudioTrack: newsystemAudioTrack, audioId: id });

      const trackChecker = setInterval(() => {
        const { room, systemAudioTrack } = this.state;

        if (!room || !systemAudioTrack) {
          clearInterval(trackChecker);
          return;
        }

        const isStillPublished = Array.from(
          room.localParticipant.audioTracks.values()
        ).some((pub) => pub.trackName === 'system-audio' && pub.isTrackEnabled);

        if (!isStillPublished) {
          console.log(
            'System audio track no longer published, republishing...'
          );
          this.republishAudioTrack();
        }
      }, 5000);

      (this as any).trackCheckerInterval = trackChecker;
    } catch (error) {
      console.error('Error capturing and sharing audio:', error);
      this.setState({ systemAudioTrack: null, audioId: null });
    }
  }

  async republishAudioTrack(): Promise<void> {
    const { room, systemAudioTrack } = this.state;

    if (!room || !systemAudioTrack) {
      return;
    }

    try {
      systemAudioTrack.enable();
      const trackPublication = await room.localParticipant.publishTrack(
        systemAudioTrack,
        { priority: 'high' }
      );
      console.log('Audio track republished successfully', {
        trackSid: trackPublication.trackSid
      });
    } catch (error) {
      console.error('Failed to republish audio track:', error);
    }
  }

  render(): ReactElement {
    const {
      status,
      room,
      participants,
      muted,
      camera,
      error,
      screenSharing,
      screenTrack,
      mutedAll
    } = this.state;
    const { children } = this.props;

    console.log('Current mutedAll state:', mutedAll);

    return (
      <CallContext.Provider
        value={{
          status,
          participants,
          localParticipant: room?.localParticipant,
          connected: room ? true : false,
          muted,
          camera,
          screenSharing,
          screenTrack,
          error,
          toggleMute: this.toggleMute,
          switchMic: this.switchMic,
          toggleCamera: this.toggleCamera,
          switchCamera: this.switchCamera,
          join: this.join,
          leave: this.leave,
          startScreenSharing: this.startScreenSharing,
          stopScreenSharing: this.stopScreenSharing,
          startAudioShare: this.startAudioShare,
          muteAllRemoteParticipants: this.muteAllRemoteParticipants,
          unmuteAllRemoteParticipants: this.unmuteAllRemoteParticipants,
          mutedAll
        }}>
        {children}
      </CallContext.Provider>
    );
  }
}

// HOC to inject auth context into CallProvider
const CallProviderWithAuth = (props: any) => {
  const auth = useContext(AuthContext);
  return <CallProvider {...props} auth={auth} />;
};

export default CallProviderWithAuth;
